import { NOT_APPLICABLE } from '@/data/constants/gtm';

export const PROMOTION_DETAILS_KEY = 'promDetails';
export const ADD_TO_CART_PARTNUMBER_PROMOTION_DETAILS_KEY = 'promoCartDtls';

type PromoCartDetail = {
	SKU: string;
	promotion_name: string;
	promotion_id: string;
	creative_name: string;
	creative_slot: string;
};

const DEFAULT_PROMOTION_DETAILS = {
	promotion_id: NOT_APPLICABLE,
	promotion_name: NOT_APPLICABLE,
	creative_name: NOT_APPLICABLE,
	creative_slot: NOT_APPLICABLE,
};

export const saveSelectedPromotionInSessionStorage = (payload: {
	promotion_id: string;
	promotion_name: string;
	creative_name: string;
	creative_slot: string;
	key_words: string[];
	list_partnumbers: [];
}) => {
	try {
		sessionStorage.setItem(PROMOTION_DETAILS_KEY, JSON.stringify(payload));
	} catch (error) {
		console.error('Error saving selected promotion in session storage:', error);
	}
};

// eslint-disable-next-line complexity
export const getSelectedPromotionFromSessionStorage = (partnumber: string) => {
	try {
		const promotionDetails = sessionStorage.getItem(PROMOTION_DETAILS_KEY);

		if (!promotionDetails) {
			return DEFAULT_PROMOTION_DETAILS;
		}

		const parsedPromotionDetails = JSON.parse(promotionDetails);

		if (!parsedPromotionDetails) {
			return DEFAULT_PROMOTION_DETAILS;
		}

		if (typeof parsedPromotionDetails !== 'object') {
			return DEFAULT_PROMOTION_DETAILS;
		}

		if (!Array.isArray(parsedPromotionDetails.list_partnumbers)) {
			return DEFAULT_PROMOTION_DETAILS;
		}

		const isIncluded = parsedPromotionDetails.list_partnumbers.includes(partnumber);

		if (!isIncluded) {
			return DEFAULT_PROMOTION_DETAILS;
		}

		return {
			promotion_id: parsedPromotionDetails.promotion_id
				? String(parsedPromotionDetails.promotion_id)
				: NOT_APPLICABLE,
			promotion_name: parsedPromotionDetails.promotion_name
				? String(parsedPromotionDetails.promotion_name)
				: NOT_APPLICABLE,
			creative_name: parsedPromotionDetails.creative_name
				? String(parsedPromotionDetails.creative_name)
				: NOT_APPLICABLE,
			creative_slot: parsedPromotionDetails.creative_slot
				? String(parsedPromotionDetails.creative_slot)
				: NOT_APPLICABLE,
		};
	} catch (error) {
		console.error('Error getting promotion details from session storage:', error);
		return DEFAULT_PROMOTION_DETAILS;
	}
};

export const isDefaultPromotionDetails = (obj: {
	promotion_id: string;
	promotion_name: string;
	creative_name: string;
	creative_slot: string;
}) =>
	obj.promotion_id === NOT_APPLICABLE &&
	obj.promotion_name === NOT_APPLICABLE &&
	obj.creative_name === NOT_APPLICABLE &&
	obj.creative_slot === NOT_APPLICABLE;

const isSamePromotion = (a: PromoCartDetail, b: PromoCartDetail) =>
	a.promotion_id === b.promotion_id &&
	a.promotion_name === b.promotion_name &&
	a.creative_name === b.creative_name &&
	a.creative_slot === b.creative_slot;

export const saveAddToCartPromotionDetailsToSessionStorage = (partnumber: string) => {
	try {
		const rawStored = sessionStorage.getItem(ADD_TO_CART_PARTNUMBER_PROMOTION_DETAILS_KEY);

		const promotionDetails = getSelectedPromotionFromSessionStorage(partnumber);

		if (isDefaultPromotionDetails(promotionDetails)) return;

		const newEntry: PromoCartDetail = {
			SKU: partnumber,
			promotion_id: promotionDetails.promotion_id,
			promotion_name: promotionDetails.promotion_name,
			creative_name: promotionDetails.creative_name,
			creative_slot: promotionDetails.creative_slot,
		};

		let parsed: PromoCartDetail[] = [];

		if (rawStored) {
			const temp = JSON.parse(rawStored);
			if (Array.isArray(temp)) {
				parsed = temp;
			}
		}

		const index = parsed.findIndex((item) => item?.SKU === partnumber);

		if (index === -1) {
			parsed.push(newEntry);
		} else {
			const existing = parsed[index];

			if (isSamePromotion(existing, newEntry)) {
				return;
			}

			parsed[index] = newEntry;
		}

		sessionStorage.setItem(ADD_TO_CART_PARTNUMBER_PROMOTION_DETAILS_KEY, JSON.stringify(parsed));
	} catch (error) {
		console.error('Error saving add to cart promotion details in session storage:', error);
	}
};

// eslint-disable-next-line complexity
export const getAddToCartPromotionDetailsFromSessionStorage = (partnumber: string) => {
	try {
		const rawStored = sessionStorage.getItem(ADD_TO_CART_PARTNUMBER_PROMOTION_DETAILS_KEY);

		if (!rawStored) {
			return DEFAULT_PROMOTION_DETAILS;
		}

		let parsed: PromoCartDetail[] = [];

		if (rawStored) {
			const temp = JSON.parse(rawStored);
			if (Array.isArray(temp)) {
				parsed = temp;
			}
		}

		const matched = parsed.find((item) => item?.SKU === partnumber);

		if (!matched) {
			return DEFAULT_PROMOTION_DETAILS;
		}

		return {
			promotion_id: matched.promotion_id ? String(matched.promotion_id) : NOT_APPLICABLE,
			promotion_name: matched.promotion_name ? String(matched.promotion_name) : NOT_APPLICABLE,
			creative_name: matched.creative_name ? String(matched.creative_name) : NOT_APPLICABLE,
			creative_slot: matched.creative_slot ? String(matched.creative_slot) : NOT_APPLICABLE,
		};
	} catch (error) {
		console.error('Error getting promotion details from session storage:', error);
		return DEFAULT_PROMOTION_DETAILS;
	}
};
